import React, { useState } from 'react';
import { CheckCircle, XCircle, Play, Loader, MessageSquare, AlertTriangle } from 'lucide-react';
import { supabase } from '../../../lib/supabase';

export default function ValidationTestingSection({ config }) {
  const [testScenarios, setTestScenarios] = useState([
    {
      id: 1,
      question: "Est-ce que vous transportez les voitures?",
      expectedKeywords: ['voiture', 'véhicule', 'conteneur complet', 'carte grise'],
      category: 'products',
      status: null,
      response: null
    },
    {
      id: 2,
      question: "Je peux envoyer un réfrigérateur?",
      expectedKeywords: ['réfrigérateur', 'accepté', 'groupage', 'emballage'],
      category: 'products',
      status: null,
      response: null
    },
    {
      id: 3,
      question: "Quels sont vos tarifs?",
      expectedKeywords: ['tarif', 'prix', 'euro', 'devis'],
      category: 'pricing',
      status: null,
      response: null
    },
    {
      id: 4,
      question: "Quel est le délai de livraison?",
      expectedKeywords: ['4-6 semaines', 'délai', 'semaine'],
      category: 'delivery',
      status: null,
      response: null
    },
    {
      id: 5,
      question: "Avez-vous des promotions?",
      expectedKeywords: ['promotion', 'réduction', 'offre'],
      category: 'pricing',
      status: null,
      response: null
    }
  ]);

  const [testing, setTesting] = useState(false);
  const [testResults, setTestResults] = useState(null);
  const [customQuestion, setCustomQuestion] = useState('');
  const [customResponse, setCustomResponse] = useState(null);
  const [customTesting, setCustomTesting] = useState(false);

  const testChatbot = async (question) => {
    try {
      const { data, error } = await supabase?.functions?.invoke('fatou-chat', {
        body: {
          messages: [
            { role: 'user', content: question }
          ]
        }
      });

      if (error) throw error;
      return data?.message || '';
    } catch (error) {
      console.error('Test error:', error);
      return `Erreur: ${error?.message}`;
    }
  };

  const validateResponse = (response, expectedKeywords) => {
    const responseLower = response?.toLowerCase();
    const matchedKeywords = expectedKeywords?.filter(keyword => 
      responseLower?.includes(keyword?.toLowerCase())
    );
    const score = (matchedKeywords?.length / expectedKeywords?.length) * 100;
    return {
      passed: score >= 50, // At least 50% of keywords should match
      score: Math.round(score),
      matchedKeywords
    };
  };

  const runAllTests = async () => {
    setTesting(true);
    const updatedScenarios = [];
    let passedCount = 0;
    let totalScore = 0;

    for (const scenario of testScenarios) {
      const response = await testChatbot(scenario?.question);
      const validation = validateResponse(response, scenario?.expectedKeywords);
      
      updatedScenarios?.push({
        ...scenario,
        response,
        status: validation?.passed ? 'passed' : 'failed',
        score: validation?.score,
        matchedKeywords: validation?.matchedKeywords
      });

      if (validation?.passed) passedCount++;
      totalScore += validation?.score;
    }

    setTestScenarios(updatedScenarios);
    setTestResults({
      total: testScenarios?.length,
      passed: passedCount,
      failed: testScenarios?.length - passedCount,
      averageScore: Math.round(totalScore / testScenarios?.length)
    });
    setTesting(false);
  };

  const testCustomQuestion = async () => {
    if (!customQuestion?.trim()) return;
    
    setCustomTesting(true);
    const response = await testChatbot(customQuestion);
    setCustomResponse(response);
    setCustomTesting(false);
  };

  const getStatusIcon = (status) => {
    if (status === 'passed') return <CheckCircle className="text-green-600" size={20} />;
    if (status === 'failed') return <XCircle className="text-red-600" size={20} />;
    return <MessageSquare className="text-gray-400" size={20} />;
  };

  const getScoreColor = (score) => {
    if (score >= 75) return 'text-green-600';
    if (score >= 50) return 'text-orange-600';
    return 'text-red-600';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <CheckCircle className="text-green-600" size={24} />
          <h2 className="text-xl font-semibold text-gray-900">Validation & Tests</h2>
        </div>
        <button
          onClick={runAllTests}
          disabled={testing}
          className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          {testing ? (
            <>
              <Loader className="animate-spin" size={18} />
              <span>Test en cours...</span>
            </>
          ) : (
            <>
              <Play size={18} />
              <span>Lancer Tous les Tests</span>
            </>
          )}
        </button>
      </div>

      <p className="text-gray-600 text-sm mb-6">
        Testez la précision et la cohérence des réponses de Fatou avec des scénarios prédéfinis. Les tests vérifient que les réponses contiennent les mots-clés attendus.
      </p>

      {/* Test Results Summary */}
      {testResults && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
          <h3 className="font-semibold text-gray-900 mb-3">Résultats des Tests</h3>
          <div className="grid grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">{testResults?.total}</div>
              <div className="text-sm text-gray-600">Total</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{testResults?.passed}</div>
              <div className="text-sm text-gray-600">Réussis</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">{testResults?.failed}</div>
              <div className="text-sm text-gray-600">Échoués</div>
            </div>
            <div className="text-center">
              <div className={`text-2xl font-bold ${getScoreColor(testResults?.averageScore)}`}>
                {testResults?.averageScore}%
              </div>
              <div className="text-sm text-gray-600">Score Moyen</div>
            </div>
          </div>
        </div>
      )}

      {/* Test Scenarios */}
      <div className="space-y-3">
        <h3 className="font-medium text-gray-900">Scénarios de Test</h3>
        {testScenarios?.map((scenario) => (
          <div key={scenario?.id} className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 mt-1">
                {getStatusIcon(scenario?.status)}
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-gray-900">{scenario?.question}</h4>
                  {scenario?.score !== undefined && (
                    <span className={`text-sm font-semibold ${getScoreColor(scenario?.score)}`}>
                      Score: {scenario?.score}%
                    </span>
                  )}
                </div>
                <div className="text-sm text-gray-600 mb-2">
                  <strong>Mots-clés attendus:</strong> {scenario?.expectedKeywords?.join(', ')}
                </div>
                {scenario?.response && (
                  <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                    <div className="text-sm font-medium text-gray-700 mb-1">Réponse de Fatou:</div>
                    <div className="text-sm text-gray-800">{scenario?.response}</div>
                    {scenario?.matchedKeywords && scenario?.matchedKeywords?.length > 0 && (
                      <div className="mt-2 text-xs text-green-700">
                        ✅ Mots-clés trouvés: {scenario?.matchedKeywords?.join(', ')}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Custom Question Test */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-medium text-gray-900 mb-3">Test Personnalisé</h3>
        <p className="text-sm text-gray-600 mb-3">
          Testez Fatou avec votre propre question pour voir comment elle répond avec la configuration actuelle.
        </p>
        <div className="space-y-3">
          <textarea
            value={customQuestion}
            onChange={(e) => setCustomQuestion(e?.target?.value)}
            placeholder="Posez une question à Fatou..."
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
          />
          <button
            onClick={testCustomQuestion}
            disabled={customTesting || !customQuestion?.trim()}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {customTesting ? (
              <>
                <Loader className="animate-spin" size={18} />
                <span>Test en cours...</span>
              </>
            ) : (
              <>
                <Play size={18} />
                <span>Tester</span>
              </>
            )}
          </button>
          {customResponse && (
            <div className="mt-3 p-3 bg-white rounded-lg border border-gray-200">
              <div className="text-sm font-medium text-gray-700 mb-1">Réponse de Fatou:</div>
              <div className="text-sm text-gray-800 whitespace-pre-wrap">{customResponse}</div>
            </div>
          )}
        </div>
      </div>

      {/* Warning */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-start space-x-2">
          <AlertTriangle className="text-yellow-600 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <p className="text-sm text-yellow-800 font-medium">Important</p>
            <p className="text-sm text-yellow-700 mt-1">
              Les tests utilisent la configuration actuelle enregistrée dans la base de données. Assurez-vous d'avoir enregistré vos modifications avant de lancer les tests pour voir les résultats à jour.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}